import React, { useState, useRef, useMemo, useEffect } from 'react';

import { getRemoteConfig } from 'firebase/remote-config';
import { apiClient, getApiEndpoint } from '../../../../api/tools/tools';
import { useAuth } from '../../../../hooks/useAuth';
import { useCart } from '../../../../hooks/useCart';
import { useTheme } from '../../../../hooks/useTheme';

import { ReactComponent as CloseIcon } from '../../../../icons/close.svg';
import { ReactComponent as Spinner } from '../../../../icons/loader.svg';
import { ReactComponent as LocationIcon } from '../../../../icons/location.svg';
import { ReactComponent as SearchIcon } from '../../../../icons/search.svg';

import './google-address-select.scss';

import { convertToAddressString } from '../../../../utils/currency/tools';
import { isValidZAPhone } from '../../../../utils/phone/phone';
import {
  getFromLocalStorage,
  saveToLocalStorage,
  STORAGE_KEYS
} from '../../../../utils/storage/storage';
import { Button } from '../../../common/button/Button';
import { Input } from '../../../common/input/Input';
import { PhoneInput } from '../../../common/phone-input/PhoneInput';
import { Textarea } from '../../../common/textarea/Textarea';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';

const MAP_LIBRARIES = ['places'];

const GoogleAddressSelect = ({ onCancel, googleApiKey }) => {
  const { getThemeVariable } = useTheme();
  const { user } = useAuth();
  const isGuest = user?.isAnonymous;

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleApiKey,
    libraries: MAP_LIBRARIES
  });
  const { setAddress } = useCart();

  const storedAddress = getFromLocalStorage(STORAGE_KEYS.DELIVERY_ADDRESS);

  const [addressComponents, setAddressComponents] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(
    storedAddress?.location ?? null
  );
  const [extraDetails, setExtraDetails] = useState(
    storedAddress ?? {
      complex: '',
      unitNumber: '',
      deliveryInstructions: '',
      contactNumber: ''
    }
  );
  const [errors, setErrors] = useState({});
  const [zoneCheckLoading, setZoneCheckLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);

  const inputRef = useRef(null);
  const autocompleteInitializedRef = useRef(false);

  // Google's Autocomplete widget exposes no "predictions fetched" event, so we approximate a
  // loading state: show it while the user is typing, and clear it early once Google's own
  // .pac-container dropdown actually gets populated (or after a timeout if it never does, so
  // the indicator itself can never look permanently stuck).
  useEffect(() => {
    if (!searchValue) {
      setSearching(false);
      return;
    }

    setSearching(true);
    const timeout = setTimeout(() => setSearching(false), 4000);
    return () => clearTimeout(timeout);
  }, [searchValue]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const container = document.querySelector('.pac-container');
      if (container && container.children.length > 0) {
        setSearching(false);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const initializeAutocomplete = () => {
    if (inputRef.current && !autocompleteInitializedRef.current) {
      autocompleteInitializedRef.current = true;
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['geocode']
      });

      autocomplete.addListener('place_changed', async () => {
        const place = autocomplete.getPlace();

        if (place.geometry) {
          const location = place.geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          setCurrentLocation({ lat, lng });
          setAddressComponents(place.address_components);
          setSearchValue('');
          setErrors((prev) => ({ ...prev, addressComponents: '', zone: '' }));

          try {
            setZoneCheckLoading(true);
            const remoteConfig = getRemoteConfig();
            const apiUrl = await getApiEndpoint(remoteConfig);
            const response = await apiClient.get(`${apiUrl}/delivery/zones/check`, {
              params: { lat, lng }
            });
            if (!response.data?.allowed) {
              setErrors((prev) => ({
                ...prev,
                zone: 'Sorry, we don\'t deliver to this address.'
              }));
            }
          } catch {
            // zone check failure is non-blocking
          } finally {
            setZoneCheckLoading(false);
          }
        }

        if (inputRef.current) inputRef.current.value = '';
      });
    }
  };

  // Attach the widget as soon as the input exists rather than waiting for the user's first
  // focus — Google's Autocomplete constructor doesn't finish wiring up its own keystroke
  // listeners synchronously, so initializing it on focus meant the very first characters typed
  // right after that focus were missed and no dropdown appeared until a later focus/blur cycle.
  useEffect(() => {
    if (isLoaded) {
      initializeAutocomplete();
    }
    //eslint-disable-next-line
  }, [isLoaded]);

  const selectedAddress = useMemo(() => {
    if (addressComponents && addressComponents.length > 0) {
      return convertToAddressString(extractAddressComponents(addressComponents));
    }
    if (storedAddress) {
      return convertToAddressString(storedAddress);
    }
    return null;
  }, [addressComponents, storedAddress]);

  const hasAddress = !!selectedAddress;

  const handleDetailChange = ({ name, value }) => {
    setExtraDetails((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateInputs = () => {
    const newErrors = {};

    if (!hasAddress) {
      newErrors.addressComponents = 'Please search for and select a delivery address.';
    }

    if (!isGuest) {
      if (!extraDetails.contactNumber || extraDetails.contactNumber.trim() === '') {
        newErrors.contactNumber = 'Contact number is required.';
      } else if (!isValidZAPhone(extraDetails.contactNumber.trim())) {
        newErrors.contactNumber = 'Enter a valid South African phone number.';
      }
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateInputs();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (errors.zone) return;

    setErrors({});

    let formData;

    if (addressComponents && addressComponents.length > 0) {
      const components = extractAddressComponents(addressComponents);
      formData = { ...extraDetails, ...components, location: currentLocation };
    } else if (storedAddress) {
      formData = { ...storedAddress, ...extraDetails };
    } else {
      setErrors({ addressComponents: 'Please select a valid address.' });
      return;
    }

    setAddress(convertToAddressString(formData, true));
    saveToLocalStorage(STORAGE_KEYS.DELIVERY_ADDRESS, formData);
    onCancel();
  };

  if (!isLoaded) {
    return (
      <div className="gas__loader">
        <Spinner width={40} height={40} style={{ color: getThemeVariable('accent-primary') }} />
      </div>
    );
  }

  return (
    <form className="gas" onSubmit={handleSubmit}>
      <div className="gas__header">
        <h2 className="gas__title">Delivery address</h2>
        <button type="button" className="gas__close" onClick={onCancel}>
          <CloseIcon width={16} height={16} />
        </button>
      </div>

      <div className="gas__body">
        {/* Search */}
        <div className="gas__search-wrap">
          <SearchIcon className="gas__search-icon" width={16} height={16} />
          <input
            ref={inputRef}
            type="text"
            className="gas__search-input"
            placeholder="Search for your delivery address…"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={initializeAutocomplete}
            autoFocus
          />
          {searching && <Spinner className="gas__search-spinner" width={16} height={16} />}
          {searchValue && (
            <button
              type="button"
              className="gas__search-clear"
              onClick={() => setSearchValue('')}
            >
              <CloseIcon width={12} height={12} />
            </button>
          )}
        </div>
        {errors.addressComponents && (
          <span className="gas__error">{errors.addressComponents}</span>
        )}

        {/* Map + selected address — only shown once an address is picked or stored */}
        {hasAddress && (
          <div className="gas__map-block">
            {currentLocation && (
              <GoogleMap
                center={currentLocation}
                zoom={16}
                mapContainerClassName="gas__map"
                options={{ disableDefaultUI: true, clickableIcons: false }}
              >
                <Marker position={currentLocation} />
              </GoogleMap>
            )}
            <div className="gas__selected">
              <LocationIcon className="gas__selected-icon" width={18} height={18} />
              <span className="gas__selected-text">{selectedAddress}</span>
            </div>
            {zoneCheckLoading && (
              <span className="gas__zone-status">Checking delivery availability…</span>
            )}
            {errors.zone && !zoneCheckLoading && (
              <span className="gas__error">{errors.zone}</span>
            )}
          </div>
        )}

        {/* Additional details */}
        <div className="gas__details">
          <span className="gas__details-label">Additional details</span>
          <div className="gas__details-row">
            <Input
              type="text"
              name="complex"
              placeholder="Complex / building name"
              value={extraDetails.complex}
              onChange={handleDetailChange}
            />
            <Input
              type="text"
              name="unitNumber"
              placeholder="Unit / apartment number"
              value={extraDetails.unitNumber}
              onChange={handleDetailChange}
            />
          </div>
          {!isGuest && (
            <div>
              <PhoneInput
                value={extraDetails.contactNumber}
                onChange={(value) => handleDetailChange({ name: 'contactNumber', value })}
                hasError={!!errors.contactNumber}
              />
              {errors.contactNumber && (
                <span className="gas__error">{errors.contactNumber}</span>
              )}
            </div>
          )}
          <Textarea
            name="deliveryInstructions"
            placeholder="Delivery instructions (optional)"
            value={extraDetails.deliveryInstructions}
            onChange={handleDetailChange}
            filled
          />
        </div>
      </div>

      <div className="gas__footer">
        <Button secondary type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Save address
        </Button>
      </div>
    </form>
  );
};

const extractAddressComponents = (components) => {
  const findComponent = (types) =>
    components.find((component) => types.some((type) => component.types.includes(type)))
      ?.long_name || '';

  return {
    streetAddress: findComponent(['street_number']) + ' ' + findComponent(['route']),
    suburb: findComponent(['sublocality', 'sublocality_level_1']),
    city: findComponent(['locality']),
    province: findComponent(['administrative_area_level_1']),
    postalCode: findComponent(['postal_code'])
  };
};

export default GoogleAddressSelect;
